/**
 * features/reports/ReportGeneration.tsx
 * ------------------------------------------------------------------
 * FEATURE #4 — ONLY admin reaches this (menu-gated by
 * canGenerateReports). Generates LKC-branded PDFs for a class,
 * uploads each to Firebase Storage, and records a ReportBatch +
 * per-student GeneratedReport docs so:
 *   - class teachers can later VIEW their class (post-generation)
 *   - admin can bulk-send (feature #6)
 * ------------------------------------------------------------------
 */
import { useState, useMemo } from 'react';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import {
  doc,
  writeBatch,
  collection,
  query,
  where,
  getDocs,
} from 'firebase/firestore';
import {
  Printer,
  Download,
  BookOpen,
  RefreshCw,
  Loader2,
  CheckCircle2,
  FileText,
  AlertTriangle,
} from 'lucide-react';
import { db, storage } from '@/lib/firebase';
import { auditFromContext } from '@/lib/audit';
import { useData, COLLECTIONS } from '@/contexts/DataContext';
import { useAuth } from '@/contexts/AuthContext';
import { assembleClassReport } from '@/lib/assembleReport';
import {
  buildClassReportPdf,
  buildSingleReportPdf,
} from '@/lib/reportPdf';
import { imposeBooklet } from '@/lib/bookletPdf';
import { useGeneratedBatches } from '@/features/reports/useGeneratedBatches';
import GeneratedBatchesList from '@/features/reports/GeneratedBatchesList';
import { loadLogoDataUrl } from '@/lib/loadLogo';
import { getExamTitle, getReportNotice } from '@/lib/assessmentConfig';
import { resolveTermDates, resolveSchoolReopens } from '@/lib/termDates';
import { Card, PageHeader, Spinner, Button } from '@/components/ui/primitives';
import { TermKey, termLabel } from '@/lib/school.config';
import type {
  GeneratedReport,
  ReportBatch,
  CommentFlag,
} from '@/types';

export default function ReportGeneration({
  year,
  term,
}: {
  year: string;
  term: TermKey;
}) {
  const { classes, activeStudents: students, subjects, teachers, users, examTitles, reportNotices, termCalendar, loading } = useData();
  const { userData } = useAuth();
  const [classId, setClassId] = useState('');
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  // P5: unresolved flags for the selected class/term block report
  // generation until resolved. `override` lets an admin proceed
  // anyway AFTER explicitly seeing the list (a stale flag must not
  // permanently freeze reports — but it can't be skipped silently).
  const [blockingFlags, setBlockingFlags] = useState<
    CommentFlag[] | null
  >(null);
  const [override, setOverride] = useState(false);
  const [checkingFlags, setCheckingFlags] = useState(false);
  const { batches, loading: batchesLoading } = useGeneratedBatches(year, term);
  const generatedIds = useMemo(() => new Set(batches.map((b) => b.classId)), [batches]);

  const cls = classes.find((c) => c.id === classId);
  const roster = useMemo(
    () =>
      students
        .filter((s) => s.currentClass === classId)
        .sort((a, b) => a.fullName.localeCompare(b.fullName)),
    [students, classId],
  );

  async function previewOne() {
    if (!classId) return;
    setBusy(true);
    setMsg(null);
    try {
      const all = await assembleClassReport({
        year,
        term,
        classId,
        classes,
        students,
        subjects,
        teachers,
        users,
        termCalendar,
      });
      if (all.length === 0) {
        setMsg({ ok: false, text: 'No students/marks for this class.' });
        return;
      }
      const logo = await loadLogoDataUrl();
      const pdf = buildSingleReportPdf(all[0], { logoDataUrl: logo, getExamTitle: (fl, t) => getExamTitle(fl, t, examTitles), getReportNotice: (y, t) => getReportNotice(y, t, reportNotices), getTermDates: (tm) => resolveTermDates(tm, termCalendar), getSchoolReopens: (tm) => resolveSchoolReopens(tm, termCalendar) });
      pdf.output('dataurlnewwindow');
    } catch (e) {
      setMsg({ ok: false, text: (e as Error).message });
    } finally {
      setBusy(false);
    }
  }

  async function downloadAll() {
    if (!classId) return;
    setBusy(true);
    setMsg(null);
    try {
      const all = await assembleClassReport({
        year,
        term,
        classId,
        classes,
        students,
        subjects,
        teachers,
        users,
        termCalendar,
      });
      if (all.length === 0) {
        setMsg({ ok: false, text: 'No students/marks for this class.' });
        return;
      }
      const logo = await loadLogoDataUrl();
      const pdf = buildClassReportPdf(all, { logoDataUrl: logo, getExamTitle: (fl, t) => getExamTitle(fl, t, examTitles), getReportNotice: (y, t) => getReportNotice(y, t, reportNotices), getTermDates: (tm) => resolveTermDates(tm, termCalendar), getSchoolReopens: (tm) => resolveSchoolReopens(tm, termCalendar) });
      pdf.save(`${cls?.name ?? classId}_${term}_${year}_reports.pdf`);
      setMsg({ ok: true, text: `Built ${all.length} reports (download).` });
    } catch (e) {
      setMsg({ ok: false, text: (e as Error).message });
    } finally {
      setBusy(false);
    }
  }

  // Bulk booklet: the same reports re-imposed as an A4 LANDSCAPE, 2-up PDF.
  // Print double-sided (flip on the short edge) and fold each sheet down the
  // middle to get an A5 saddle-fold booklet per pupil (cover, 2, 3, back).
  async function downloadBooklet() {
    if (!classId) return;
    setBusy(true);
    setMsg(null);
    try {
      const all = await assembleClassReport({
        year, term, classId, classes, students, subjects, teachers, users, termCalendar,
      });
      if (all.length === 0) {
        setMsg({ ok: false, text: 'No students/marks for this class.' });
        return;
      }
      const logo = await loadLogoDataUrl();
      const pdf = buildClassReportPdf(all, { logoDataUrl: logo, getExamTitle: (fl, t) => getExamTitle(fl, t, examTitles), getReportNotice: (y, t) => getReportNotice(y, t, reportNotices), getTermDates: (tm) => resolveTermDates(tm, termCalendar), getSchoolReopens: (tm) => resolveSchoolReopens(tm, termCalendar) });
      const booklet = await imposeBooklet(pdf.output('arraybuffer'));
      const blob = new Blob([booklet as BlobPart], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${cls?.name ?? classId}_${term}_${year}_booklet_A5.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      setMsg({ ok: true, text: `Built ${all.length} booklets (A4 landscape, 2-up). Print double-sided — flip on SHORT edge — then fold each sheet.` });
    } catch (e) {
      setMsg({ ok: false, text: (e as Error).message });
    } finally {
      setBusy(false);
    }
  }

  // P5: flags for this class+term whose status is not 'resolved'.
  async function fetchUnresolvedFlags(cid: string): Promise<CommentFlag[]> {
    const q = query(
      collection(db, COLLECTIONS.flags),
      where('classId', '==', cid),
      where('year', '==', year),
      where('term', '==', term),
    );
    const snap = await getDocs(q);
    return snap.docs
      .map((d) => ({ id: d.id, ...(d.data() as object) }) as CommentFlag)
      .filter((f) => f.status !== 'resolved');
  }

  // Generate (or REGENERATE) a class's reports. `cidArg` lets the batch list's
  // Regenerate button target any class in one click; without it, the selected
  // class is used. Roster/name are resolved for the target class here.
  async function generateAndStore(cidArg?: string) {
    const cid = cidArg ?? classId;
    if (!cid) return;
    setClassId(cid);
    const genCls = classes.find((c) => c.id === cid);
    const genRoster = students
      .filter((s) => s.currentClass === cid)
      .sort((a, b) => a.fullName.localeCompare(b.fullName));

    // P5 GATE: don't generate official reports while flagged
    // issues for this class/term are unresolved — unless an admin
    // has explicitly chosen to override after seeing the list.
    if (!override) {
      setCheckingFlags(true);
      try {
        const open = await fetchUnresolvedFlags(cid);
        if (open.length > 0) {
          setBlockingFlags(open);
          setCheckingFlags(false);
          return; // blocked — UI shows the list + override option
        }
      } catch (e) {
        setCheckingFlags(false);
        setMsg({
          ok: false,
          text: `Could not verify flags before generating: ${
            (e as Error).message
          }`,
        });
        return; // fail safe: don't generate if we can't verify
      }
      setCheckingFlags(false);
    }
    setBlockingFlags(null);

    setBusy(true);
    setMsg(null);
    try {
      const all = await assembleClassReport({
        year,
        term,
        classId: cid,
        classes,
        students,
        subjects,
        teachers,
        users,
        termCalendar,
      });
      if (all.length === 0) {
        setMsg({ ok: false, text: 'No students/marks for this class.' });
        return;
      }
      setProgress({ done: 0, total: all.length });

      const batch = writeBatch(db);
      const batchId = `${year}-${term}-${cid}`;
      const generatedReports: GeneratedReport[] = [];
      const logo = await loadLogoDataUrl();

      for (let i = 0; i < all.length; i++) {
        const rs = all[i];
        const stu = genRoster[i];
        // 4-page A5 booklet per student
        const onePdf = buildSingleReportPdf(rs, { logoDataUrl: logo, getExamTitle: (fl, t) => getExamTitle(fl, t, examTitles), getReportNotice: (y, t) => getReportNotice(y, t, reportNotices), getTermDates: (tm) => resolveTermDates(tm, termCalendar), getSchoolReopens: (tm) => resolveSchoolReopens(tm, termCalendar) });
        const blob = onePdf.output('blob');

        const path = `reports/${year}/${term}/${cid}/${stu.id}.pdf`;
        const storageRef = ref(storage, path);
        await uploadBytes(storageRef, blob, {
          contentType: 'application/pdf',
        });
        const url = await getDownloadURL(storageRef);

        const gr: GeneratedReport = {
          id: `${year}-${term}-${cid}-${stu.id}`,
          year,
          term,
          classId: cid,
          className: genCls?.name ?? cid,
          studentId: stu.id,
          studentName: stu.fullName,
          storagePath: path,
          downloadUrl: url,
          generatedAt: new Date().toISOString(),
          generatedById: userData?.uid ?? '',
          generatedByName: userData?.displayName ?? '',
          send: { email: 'not_sent', whatsapp: 'not_sent' },
        };
        generatedReports.push(gr);
        batch.set(
          doc(db, COLLECTIONS.generatedReports, gr.id),
          gr,
          { merge: true },
        );
        setProgress({ done: i + 1, total: all.length });
      }

      const batchDoc: ReportBatch = {
        id: batchId,
        year,
        term,
        classId: cid,
        className: genCls?.name ?? cid,
        studentIds: genRoster.map((s) => s.id),
        generatedAt: new Date().toISOString(),
        generatedById: userData?.uid ?? '',
        generatedByName: userData?.displayName ?? '',
        status: 'ready',
        ...(override
          ? {
              flagOverride: true,
              flagOverrideCount: blockingFlags?.length ?? 0,
            }
          : {}),
      };
      batch.set(doc(db, COLLECTIONS.reportBatches, batchId), batchDoc, {
        merge: true,
      });
      await batch.commit();
      auditFromContext(userData, 'reports_generated', 'academic', {
        targetId: cid, targetType: 'report',
        details: { year, term, count: all.length, flagOverride: !!override },
      });

      setMsg({
        ok: true,
        text: `Generated & stored ${all.length} reports. Class teachers can now view them; ready for bulk send.${
          override ? ' (Generated with flag override — recorded.)' : ''
        }`,
      });
      // reset the gate so it re-checks for the next class
      setOverride(false);
      setBlockingFlags(null);
    } catch (e) {
      setMsg({ ok: false, text: (e as Error).message });
    } finally {
      setBusy(false);
      setProgress({ done: 0, total: 0 });
    }
  }

  if (loading) return <Spinner label="Loading…" />;

  return (
    <div>
      <PageHeader
        title="Generate Reports"
        subtitle={`${termLabel(term)} ${year} · admin only — generated PDFs are stored for class-teacher viewing & bulk send`}
      />

      {msg && (
        <div
          className={`mb-4 text-sm rounded-xl px-3 py-2.5 border flex items-center gap-2 ${
            msg.ok
              ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
              : 'bg-red-50 text-red-700 border-red-200'
          }`}
        >
          {msg.ok ? <CheckCircle2 size={15} /> : <FileText size={15} />}
          {msg.text}
        </div>
      )}

      <div className="mb-4">
        <GeneratedBatchesList
          batches={batches}
          loading={batchesLoading}
          title="Already generated this term"
          emptyText="No classes generated yet this term."
          highlightId={classId ? `${year}-${term}-${classId}` : undefined}
          renderAction={(b) => (
            <Button
              variant="secondary"
              icon={<RefreshCw size={14} className={busy ? 'animate-spin' : ''} />}
              disabled={busy || checkingFlags}
              onClick={() => generateAndStore(b.classId)}
            >
              Regenerate
            </Button>
          )}
        />
      </div>

      <Card className="p-5 mb-4">
        <label className="text-sm font-medium">Class</label>
        <select
          value={classId}
          onChange={(e) => {
            setClassId(e.target.value);
            setBlockingFlags(null);
            setOverride(false);
            setMsg(null);
          }}
          className="w-full sm:w-72 mt-1 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 dark:bg-gray-800"
        >
          <option value="">— select class —</option>
          {classes.filter((c) => !c.archived).map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}{generatedIds.has(c.id) ? '  ✓ generated' : ''}
            </option>
          ))}
        </select>
        {classId && (
          <p className="text-xs text-gray-500 mt-2">
            {roster.length} students in {cls?.name}.
          </p>
        )}
      </Card>

      {classId && (
        <Card className="p-5">
          <div className="flex flex-wrap gap-3">
            <Button
              variant="secondary"
              icon={<FileText size={16} />}
              disabled={busy}
              onClick={previewOne}
            >
              Preview first report
            </Button>
            <Button
              variant="secondary"
              icon={<Download size={16} />}
              disabled={busy}
              onClick={downloadAll}
            >
              Download all (single PDF)
            </Button>
            <Button
              variant="secondary"
              icon={<BookOpen size={16} />}
              disabled={busy}
              onClick={downloadBooklet}
            >
              Download booklet (A5 fold)
            </Button>
            <Button
              icon={
                busy || checkingFlags ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <Printer size={16} />
                )
              }
              disabled={busy || checkingFlags}
              onClick={() => generateAndStore()}
            >
              {checkingFlags
                ? 'Checking flags…'
                : override
                  ? 'Generate anyway'
                  : 'Generate & store reports'}
            </Button>
          </div>

          {blockingFlags && blockingFlags.length > 0 && (
            <div className="mt-4 rounded-xl border-2 border-red-300 bg-red-50 dark:bg-red-900/30 dark:border-red-700 p-4">
              <div className="flex items-start gap-2">
                <AlertTriangle
                  size={18}
                  className="text-red-600 dark:text-red-400 mt-0.5 shrink-0"
                />
                <div className="flex-1">
                  <p className="font-bold text-red-800 dark:text-red-200">
                    Report generation blocked —{' '}
                    {blockingFlags.length} unresolved flag
                    {blockingFlags.length === 1 ? '' : 's'} for{' '}
                    {cls?.name}, {termLabel(term)} {year}
                  </p>
                  <p className="text-sm text-red-700 dark:text-red-300 mt-1">
                    These should be resolved in Comment Flags
                    before official reports go out:
                  </p>
                  <ul className="mt-2 space-y-1 max-h-44 overflow-y-auto">
                    {blockingFlags.map((f) => (
                      <li
                        key={f.id}
                        className="text-xs text-red-700 dark:text-red-300 flex items-start gap-1.5"
                      >
                        <span className="mt-1 w-1.5 h-1.5 rounded-full bg-red-500 shrink-0" />
                        <span>
                          <strong>{f.studentName}</strong>
                          {f.subjectName
                            ? ` · ${f.subjectName}`
                            : ''}{' '}
                          — {f.reasonLabel || f.reason}{' '}
                          <span className="text-red-500">
                            ({f.status})
                          </span>
                        </span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-3 flex flex-wrap items-center gap-3">
                    <button
                      onClick={() => {
                        setBlockingFlags(null);
                        setOverride(false);
                      }}
                      className="text-xs px-3 py-1.5 rounded-lg border border-red-300 text-red-700 dark:border-red-600 dark:text-red-300"
                    >
                      OK, I'll resolve them first
                    </button>
                    <label className="flex items-center gap-2 text-xs text-red-800 dark:text-red-200">
                      <input
                        type="checkbox"
                        checked={override}
                        onChange={(e) =>
                          setOverride(e.target.checked)
                        }
                      />
                      Override: generate anyway (recorded — use
                      only if these flags are known-stale)
                    </label>
                  </div>
                </div>
              </div>
            </div>
          )}

          {busy && progress.total > 0 && (
            <div className="mt-4">
              <div className="h-2 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
                <div
                  className="h-full bg-primary transition-all"
                  style={{
                    width: `${(progress.done / progress.total) * 100}%`,
                  }}
                />
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Uploading {progress.done} / {progress.total}…
              </p>
            </div>
          )}

          <p className="text-xs text-gray-500 mt-4">
            <strong>Preview / Download</strong> are local only — nothing
            is stored. <strong>Generate &amp; store</strong> renders each
            report, uploads it to secure storage, and unlocks
            class-teacher viewing and bulk send for this class.
          </p>
        </Card>
      )}
    </div>
  );
}
